<template>
	<h5 class="breakout__description">
		Your Color Mix (choose up to 4 colors)
	</h5>
	<div class="breakouts">
		<ColorBreakout
			v-for="( selectedColor, i ) in selectedColors"
			:id="selectedColor.id"
			:key="selectedColor.id"
			:index="selectedColor.placementIndex"
			:title="selectedColor.title"
			:image="selectedColor.thumb_url"
			:percentage="selectedColor.percentage"
			:selectable-colors="selectableColors"
			:can-trash="canTrashBreakout( i )"
			:flyout-instructions="flyoutInstructions"
			:freeze-slider="freezeSlider( i )"
			:steps="calculateSliderSteps()"
			@update:percentage="handleBreakoutPercentageChange"
			@change:color="handleBreakoutChangeColor"
			@trash="handleTrashBreakout"
		/>
		<AddBreakout
			v-if="canAddBreakouts"
			:selectable-colors="selectableColors"
			:flyout-instructions="flyoutInstructions"
			@select:color="handleSelectColorForBreakout"
		/>
	</div>
	<div
		v-if="showPercentageTotalWarning"
		class="breakout__warning"
	>
		Your breakouts must add up to 100%!
		<a
			href="#"
			class="breakout__warning__equalize"
			@click="handleClickEqualize"
		>
			<strong>Click here to equally divide colors.</strong>
		</a>
	</div>


	<div class="view-color-mix">
		<button
			class="btn btn-secondary"
			@click="emit( 'breakouts:view:mixer' )"
		>
			View Color Mix
		</button>
	</div>
</template>


<style lang="scss">
.breakout {
	&__description {
		margin-bottom: 24px;
	}

	&__warning {
		margin-bottom: 48px;
		padding: 24px 16px;
		color: white;
		background-color: #B52B30;
		border-radius: 6px;

		&__equalize {
			color: white;

			&:hover {
				border-color: transparent;
			}
		}
	}
}

.breakouts {
	display: grid;
	grid-template-columns: 1fr 1fr;
	row-gap: 10px;
	column-gap: 24px;
	margin-bottom: 32px;

	@media( min-width: 768px ) {
		column-gap: 48px;
	}
}

.view-color-mix {
	margin-bottom: 24px;
	text-align: center;

	@media( min-width: 1024px ) {
		display: none;
	}
}
</style>


<script setup>
import { computed } from 'vue'
import AddBreakout from './AddBreakout.vue'
import ColorBreakout from './ColorBreakout.vue'


const props = defineProps({
	activeColors: {
		type: Array,
		required: true,
	},
	selectedColors: {
		type: Array,
		required: true,
	},
	flyoutInstructions: {
		type: String,
		default: '',
	},
})


const emit = defineEmits(
	[
		'breakout:update:percentage',
		'breakout:select:color',
		'breakout:change:color',
		'breakout:trash',
		'breakouts:percentages:equalize',
		'breakouts:view:mixer',
	],
)


// eslint-disable-next-line arrow-body-style
const selectableColors = computed( () => {
	return props.activeColors.filter( color => !color.selected )
})


// eslint-disable-next-line arrow-body-style
const canAddBreakouts = computed( () => {
	return 4 > props.selectedColors.length
})


// eslint-disable-next-line arrow-body-style
const totalColorPercentage = computed( () => {
	return props.selectedColors.reduce( ( total, breakout ) => total + breakout.percentage, 0 )
})


const showPercentageTotalWarning = computed( () => {
	if ( 100 === totalColorPercentage.value ) {
		return false
	}

	if ( 3 === props.selectedColors.length && 99 === totalColorPercentage.value ) {
		return false
	}

	return true
})


/**
 * Calculate if breakout can be trashed depending on certain conditions
 *
 * @param {number} index
 * @return {bool} True, if can trash
 */
const canTrashBreakout = index => {

	// if only one color, can't trash
	if ( 1 === props.selectedColors.length && 0 === index ) {
		return false
	}

	return true
}


/**
 * Calculate if breakout slider should be frozen/non-interactive
 *
 * @return {number}
 */
const calculateSliderSteps = () => {
	if ( 2 === props.selectedColors.length || 4 === props.selectedColors.length ) {
		return 5
	}

	return 1
}


/**
 * Calculate slider steps based on breakout count
 *
 * @param {number} index
 * @return {bool} True, if should be frozen
 */
// eslint-disable-next-line arrow-body-style
const freezeSlider = index => {
	return 1 === props.selectedColors.length && 0 === index
}


/**
 * Handle when user changes percentage for breakout
 *
 * @param {number} colorId
 * @param {number} percentage
 *
 * @return {void}
 */
const handleBreakoutPercentageChange = ( colorId, percentage ) => {
	emit( 'breakout:update:percentage', colorId, percentage )
}


/**
 * Handle user clearing breakout
 *
 * @param {number} colorId
 * @return {void}
 */
const handleTrashBreakout = colorId => {
	emit( 'breakout:trash', colorId )
}


/**
 * Handle when user adds additional breakouts
 *
 * @param {number} colorId
 * @return {void}
 */
const handleSelectColorForBreakout = colorId => {
	if ( !colorId ) {
		return
	}

	// confirm color isn't already selected
	if ( props.selectedColors.find( color => color.id === colorId ) ) {
		return
	}

	// confirm we can add new breakout
	if ( 3 < props.selectedColors.length ) {
		return
	}

	emit( 'breakout:select:color', colorId )
}


/**
 * Handle user clicking "Equalize" link/button
 *
 * @param {event} e
 * @return {void}
 */
const handleClickEqualize = e => {
	e.preventDefault()

	emit( 'breakouts:percentages:equalize' )
}


/**
 * Handle user selecting different color for (already active) breakout
 *
 * @param {number} newColorId
 * @param {number} placementIndex
 * @param {number} percentage
 * @param {number} oldColorId
 *
 * @return {void}
 */
const handleBreakoutChangeColor = ( newColorId, placementIndex, percentage, oldColorId ) => {
	emit( 'breakout:change:color', newColorId, placementIndex, percentage, oldColorId )
}

</script>
